import TranslateIcon from "@mui/icons-material/Translate";
import {
  Box,
  Chip,
  CircularProgress,
  FormHelperText,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { APP_IMAGES } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import CustomSelect from "../../../components/customSelect";
import Label from "../../../components/Label";
import NavigationDrawer from "../../../components/NavigationDrawer";
import RichTextEditor from "../../../components/TextEditor";
import TextInput from "../../../components/TextInputs";
import { ALL_BLOG_CATEGORY } from "../../../constants/Variables";
import globalStyle from "../../../globalStyle";
import { baseUrl } from "../../../services";
import {
  createBlog,
  getBlogDetail,
  suggestBlog,
  updateBlog,
} from "../../../services/blogsApiFn";
import { decodeHtml, handleOpenSnackBar } from "../../../utils";
import { translateText } from "../../../utils/translationService";
import { addEditBlogValidation } from "../../../utils/Validation";
import AddEditFaqDialog from "./AddEditFaqDialog";
import AttachedBasePackageDialog from "./AttachedBasePackageDialog";
import AttachedBasePackages from "./AttachedBasePackages";
import AttachedProducts from "./AttachedProducts";
import AttachedProductsDialog from "./AttachedProductsDialog";
import BlogsFaqs from "./Faqs";
import Attachments from "./Attachments";
import { getBlogTopicList } from "../../../services/blogTopicApiFn";

const initialInputValues = {
  image: null,
  title_en: "",
  title_ro: "",
  // category: "",
  topic: "",
  smallDescription_en: "",
  smallDescription_ro: "",
  description_en: "",
  description_ro: "",
  blogPrompt: "",
};

const AddEditBlog = ({}) => {
  const navigate = useNavigate();
  const { type, id } = useParams();
  const imageRef = useRef(null);
  const errorRef = useRef(null);
  const attachedProductsDialogRef = useRef(null);
  const attachedBasePackagesDialogRef = useRef(null);
  const faqsDialogRef = useRef(null);
  const { t } = useTranslation(["blog", "common"]);
  const [isLoading, setIsLoading] = useState({
    save: false,
    imageUpload: false,
    generateBlog: false,
    translateToEnglish: false,
  });
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialInputValues);
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  // Search Input For Products
  const [blogTopicList, setBlogTopicList] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedBasePackages, setSelectedBasePackages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const handleFetchBlogTopicList = async () => {
    try {
      const response = await getBlogTopicList();
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        setBlogTopicList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    handleFetchBlogTopicList();
  }, []);

  useEffect(() => {
    if (type === "edit") {
      fetchBlogDetail();
    }
  }, [type, id]);

  const fetchBlogDetail = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await getBlogDetail(id);
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        setInputValues({
          image: data?.image,
          title_en: data?.title?.en,
          title_ro: data?.title?.ro,
          // category: data?.category,
          topic: data?.topic || "",
          smallDescription_en: data?.smallDescription?.en,
          smallDescription_ro: data?.smallDescription?.ro,
          description_en: decodeHtml(data?.description?.en),
          description_ro: decodeHtml(data?.description?.ro),
        });
        setFaqs(data?.faqs || []);
        setSelectedProducts(data?.products || []);
        setSelectedBasePackages(data?.basePackages || []);
        setAttachments(data?.attachments || []);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const maxSize = 3 * 1024 * 1024; // 3MB

    if (file?.size > maxSize) {
      return handleOpenSnackBar(
        "error",
        t("validation.imageSizeLimit", { ns: "common" }),
      );
    }

    const imageType = file?.type.split("/")[1].toLowerCase();
    const allowedFormats = ["png", "jpeg", "jpg", "webp"];

    if (!allowedFormats.includes(imageType)) {
      return handleOpenSnackBar(
        "error",
        t("validation.allowedImageFormats", { ns: "common" }),
      );
    }

    try {
      setIsLoading((prev) => ({ ...prev, imageUpload: true }));
      const formData = new FormData();
      formData.append("image", file);

      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setInputError((prev) => ({ ...prev, image: "" }));
      setInputValues((prev) => ({ ...prev, image: response?.data?.url }));
      imageRef.current.value = null;
      handleOpenSnackBar(
        "success",
        t("success.imageUploadSuccess", { ns: "common" }),
      );
    } catch (error) {
      imageRef.current.value = null;
      handleOpenSnackBar(
        "error",
        t("error.imageUploadFailed", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, imageUpload: false }));
    }
  };

  const scrollToError = () => {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.focus();
        }
      }, 300);
    }
  };

  const handleAddEditBlog = async () => {
    errorRef.current = null;
    const validate = addEditBlogValidation(
      inputValues,
      setInputError,
      errorRef,
    );

    if (validate) {
      scrollToError();
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }

    let productsIds = [];
    let basePackagesIds = [];
    let faqsPayload = [];

    if (selectedProducts.length > 0) {
      productsIds = selectedProducts.map((product) => product._id);
    }

    if (selectedBasePackages.length > 0) {
      basePackagesIds = selectedBasePackages.map(
        (basePackage) => basePackage._id,
      );
    }

    if (faqs.length > 0) {
      faqsPayload = faqs.map((faq) => ({
        question: {
          en: faq.question?.en,
          ro: faq.question?.ro,
        },
        answer: {
          en: faq.answer?.en,
          ro: faq.answer?.ro,
        },
      }));
    }

    const payload = {
      image: inputValues.image,
      // category: inputValues.category,
      topic: inputValues.topic,
      title: {
        en: inputValues.title_en,
        ro: inputValues.title_ro,
      },
      smallDescription: {
        en: inputValues.smallDescription_en,
        ro: inputValues.smallDescription_ro,
      },
      description: {
        en: inputValues.description_en,
        ro: inputValues.description_ro,
      },
      products: productsIds,
      basePackages: basePackagesIds,
      faqs: faqsPayload,
      attachments,
    };

    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response =
        type === "add"
          ? await createBlog(payload)
          : await updateBlog(id, payload);
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response?.data.message);
        navigate(-1);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleSuggestBlog = async () => {
    if (!inputValues.blogPrompt)
      return handleOpenSnackBar("error", t("validation.pleaseEnterBlogPrompt"));

    try {
      setIsLoading((prev) => ({ ...prev, generateBlog: true }));
      handleOpenSnackBar("info", t("generatingBlog"));
      const response = await suggestBlog({
        blogPrompt: inputValues.blogPrompt,
      });
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        setInputValues((prev) => ({
          ...prev,
          description_en: data?.englishDescription,
          description_ro: data?.romanianDescription,
        }));
        setInputError((prev) => ({
          ...prev,
          description_en: "",
          description_ro: "",
        }));
        handleOpenSnackBar("success", response?.data.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, generateBlog: false }));
    }
  };

  const handleEditorChangeEnglish = (data) => {
    setInputError((err) => ({ ...err, description_en: "" }));
    setInputValues((val) => ({ ...val, description_en: data }));
  };

  const handleEditorChangeRomanian = (data) => {
    setInputError((err) => ({ ...err, description_ro: "" }));
    setInputValues((val) => ({ ...val, description_ro: data }));
  };

  const handleChangeInputValues = (event) => {
    const { name, value } = event.target;
    setInputError((prev) => ({ ...prev, [name]: "" }));
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    if (!inputValues[key1]) {
      return handleOpenSnackBar(
        "error",
        t("validation.valueRequired", { ns: "common" }),
      );
    }

    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.errorWhileTranslating", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading.save} />
      <PaperContainer
        title={type === "add" ? t("addNewBlog") : t("editBlog")}
        subtitle={
          type === "add" ? t("addNewBlogDescription") : t("editBlogDescription")
        }
        showBackButton={true}
        onBack={() => navigate("/blogs")}
      >
        {/* Blog Image Upload Container */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Label labelText={t("blogImage")} required htmlFor={"blog-image"} />
          <Stack
            justifyContent={"center"}
            alignItems={"center"}
            border={`1px solid ${inputError.image ? "#d32f2f" : "#d7d7d7"}`}
            sx={{
              cursor: "pointer",
              height: { xs: 200, md: 200 },
              padding: 1,
              borderRadius: 1,
            }}
            onClick={() => imageRef.current.click()}
          >
            {inputValues.image ? (
              <img
                src={inputValues.image}
                alt={"blog-image"}
                width={"100%"}
                height={"100%"}
                style={{
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
            ) : (
              <>
                <img
                  src={APP_IMAGES.uploadImage}
                  alt={"upload-image"}
                  width={100}
                />
                <span style={{ marginTop: "8px", color: "#666" }}>
                  {t("uploadBlogImage")}
                </span>
              </>
            )}
          </Stack>
          {Boolean(inputError.image) && (
            <FormHelperText sx={{ color: "#d32f2f" }}>
              {t(inputError.image)}
            </FormHelperText>
          )}
          <input
            type="file"
            ref={imageRef}
            onChange={handleImageUpload}
            style={{ display: "none" }}
            accept="image/*"
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Label labelText={t("blogTopic")} htmlFor={"blog-topic"} />
            <CustomSelect
              name={"topic"}
              value={inputValues.topic}
              onChange={handleChangeInputValues}
              inputError={Boolean(inputError.topic)}
              helperText={t(inputError.topic)}
              formStyleProps={{
                "& .MuiSelect-select":{
                  textTransform:"capitalize"
                }
              }}
            >
              {blogTopicList.map((val, ind) => {
                return (
                  <MenuItem
                    key={ind}
                    value={val._id}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {val.title[currentLanguage]}
                  </MenuItem>
                );
              })}
            </CustomSelect>
          </Grid>
          <Grid item xs={12}>
            <Label
              labelText={t("titleInRomanian")}
              required
              htmlFor={"blog-title-ro"}
            />
            <TextInput
              id={"blog-title-ro"}
              styleProps={globalStyle.textField}
              placeholder={t("enterTitleInRomanian")}
              required
              textLength={255}
              name={"title_ro"}
              value={inputValues.title_ro}
              onChange={handleChangeInputValues}
              inputError={Boolean(inputError.title_ro)}
              helperText={t(inputError.title_ro)}
              InputEndIcon={
                <Typography variant="body2" color="text.grey">
                  {inputValues.title_ro?.length}/255
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              justifyContent="space-between"
              mb={"5px"}
            >
              <Label
                labelText={t("titleInEnglish")}
                required
                htmlFor={"blog-title-en"}
                labelProps={{
                  marginBottom: 0,
                }}
              />
              <Chip
                label={t("translateToEnglish", { ns: "common" })}
                size="small"
                onClick={() => handleTranslateToEnglish("title_ro", "title_en")}
                disabled={isLoading.translateToEnglish}
                icon={
                  isLoading.translateToEnglish ? (
                    <CircularProgress size={16} color="primary" />
                  ) : (
                    <TranslateIcon />
                  )
                }
              />
            </Stack>
            <TextInput
              id={"blog-title-en"}
              styleProps={globalStyle.textField}
              placeholder={t("enterTitleInEnglish")}
              required
              textLength={255}
              name={"title_en"}
              value={inputValues.title_en}
              onChange={handleChangeInputValues}
              inputError={Boolean(inputError.title_en)}
              helperText={t(inputError.title_en)}
              InputEndIcon={
                <Typography variant="body2" color="text.grey">
                  {inputValues.title_en?.length}/255
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Label
              labelText={t("smallDescriptionInRomanian")}
              required
              htmlFor={"blog-smallDescription-ro"}
            />
            <TextInput
              id={"blog-smallDescription-ro"}
              styleProps={globalStyle.textField}
              placeholder={t("enterSmallDescriptionInRomanian")}
              required
              multiline
              rows={4}
              textLength={500}
              name={"smallDescription_ro"}
              value={inputValues.smallDescription_ro}
              onChange={handleChangeInputValues}
              inputError={Boolean(inputError.smallDescription_ro)}
              helperText={t(inputError.smallDescription_ro)}
              InputEndIcon={
                <Typography variant="body2" color="text.grey">
                  {inputValues.smallDescription_ro?.length}/500
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              justifyContent="space-between"
              mb={"5px"}
            >
              <Label
                labelText={t("smallDescriptionInEnglish")}
                required
                htmlFor={"blog-smallDescription-en"}
                labelProps={{
                  marginBottom: 0,
                }}
              />
              <Chip
                label={t("translateToEnglish", { ns: "common" })}
                size="small"
                onClick={() =>
                  handleTranslateToEnglish(
                    "smallDescription_ro",
                    "smallDescription_en",
                  )
                }
                disabled={isLoading.translateToEnglish}
                icon={
                  isLoading.translateToEnglish ? (
                    <CircularProgress size={16} color="primary" />
                  ) : (
                    <TranslateIcon />
                  )
                }
              />
            </Stack>
            <TextInput
              id={"blog-smallDescription-en"}
              styleProps={globalStyle.textField}
              placeholder={t("enterSmallDescriptionInEnglish")}
              required
              multiline
              rows={4}
              textLength={500}
              name={"smallDescription_en"}
              value={inputValues.smallDescription_en}
              onChange={handleChangeInputValues}
              inputError={Boolean(inputError.smallDescription_en)}
              helperText={t(inputError.smallDescription_en)}
              InputEndIcon={
                <Typography variant="body2" color="text.grey">
                  {inputValues.smallDescription_en?.length}/500
                </Typography>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Label labelText={t("blogPrompt")} />
            <TextInput
              id={"blog-prompt"}
              styleProps={{
                ...globalStyle.textField,
                "& .MuiOutlinedInput-root": {
                  paddingRight: 0,
                },
              }}
              placeholder={t("enterBlogPrompt")}
              required
              name={"blogPrompt"}
              value={inputValues.blogPrompt}
              onChange={handleChangeInputValues}
              InputEndIcon={
                <GlobalButton
                  btnText={
                    isLoading.generateBlog ? t("generatingBlog") : t("generate")
                  }
                  styleProps={{
                    height: "auto",
                    borderRadius: 1,
                  }}
                  handleClickBtn={handleSuggestBlog}
                  disabled={isLoading.generateBlog}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Label
              required
              labelText={t("longDescriptionInEnglish")}
              htmlFor={"blog-long-description-en"}
            />
            <Box id="blog-description-en" tabIndex={-1}>
              <RichTextEditor
                value={inputValues.description_en || ""}
                onChange={handleEditorChangeEnglish}
                placeholder={t("enterLongDescriptionInEnglish")}
                height={420}
              />
            </Box>

            {Boolean(inputError.description_en) && (
              <FormHelperText error>
                {t(inputError.description_en)}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12}>
            <Label
              required
              labelText={t("longDescriptionInRomanian")}
              htmlFor={"blog-long-description-ro"}
            />
            <Box id="blog-description-ro" tabIndex={-1}>
              <RichTextEditor
                value={inputValues.description_ro || ""}
                onChange={handleEditorChangeRomanian}
                placeholder={t("enterLongDescriptionInRomanian")}
                height={420}
              />
            </Box>

            {Boolean(inputError.description_ro) && (
              <FormHelperText error>
                {t(inputError.description_ro)}
              </FormHelperText>
            )}
          </Grid>
        </Grid>

        <AttachedProducts
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          attachedProductsDialogRef={attachedProductsDialogRef}
        />

        <AttachedBasePackages
          selectedBasePackages={selectedBasePackages}
          setSelectedBasePackages={setSelectedBasePackages}
          attachedBasePackagesDialogRef={attachedBasePackagesDialogRef}
        />

        <BlogsFaqs
          faqs={faqs}
          setFaqs={setFaqs}
          faqsDialogRef={faqsDialogRef}
        />

        <Attachments
          attachments={attachments}
          setAttachments={setAttachments}
        />

        <Stack
          mt={10}
          mb={3}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"flex-end"}
        >
          <Stack minWidth={120} height={40} mr={2}>
            <GlobalButton
              styleProps={{
                backgroundColor: "#FFF",
                color: "primary.main",
              }}
              btnText={"discard"}
              hoverProperties={{
                color: "primary.main",
                backgroundColor: "#FFF",
              }}
              handleClickBtn={() => navigate("/blogs")}
            />
          </Stack>
          <Stack minWidth={120} height={40}>
            <GlobalButton btnText={"save"} handleClickBtn={handleAddEditBlog} />
          </Stack>
        </Stack>

        <AttachedProductsDialog
          ref={attachedProductsDialogRef}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
        />
        <AttachedBasePackageDialog
          ref={attachedBasePackagesDialogRef}
          selectedBasePackages={selectedBasePackages}
          setSelectedBasePackages={setSelectedBasePackages}
        />
        <AddEditFaqDialog ref={faqsDialogRef} faqs={faqs} setFaqs={setFaqs} />
      </PaperContainer>
    </NavigationDrawer>
  );
};

export default AddEditBlog;
